import { useNavigate } from 'react-router-dom';
import { Chapter } from '@/types';

interface TOCDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  bookId: string;
  book: {
    title?: string;
    author?: string;
  } | null | undefined;
  chapters: Chapter[];
  currentChapterSeq: number;
}

export function TOCDrawer({ 
  isOpen, 
  onClose, 
  bookId, 
  book, 
  chapters, 
  currentChapterSeq 
}: TOCDrawerProps) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex flex-col justify-end"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fade-in" />
      <div 
        className="relative bg-gradient-to-t from-[var(--bg-primary)] to-[var(--bg-secondary)] rounded-t-3xl p-6 max-h-[85vh] overflow-hidden flex flex-col shadow-2xl animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        <div className="w-12 h-1 bg-[var(--border)] rounded-full mx-auto mb-6 shrink-0" />
        
        <div className="flex items-center justify-between mb-6 shrink-0">
          <div>
            <h3 className="font-body text-lg font-medium text-[var(--text-primary)]">{book?.title}</h3>
            <p className="font-ui text-xs text-[var(--text-tertiary)] mt-1">by {book?.author}</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-full text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="overflow-y-auto space-y-1 pb-8 -mx-2 px-2">
          {chapters.map((ch) => {
            const isCurrent = ch.sequence === currentChapterSeq;
            // Use server-side isUnlocked property for security
            const isUnlocked = ch.tokenCost === 0 || ch.isUnlocked === true;
            
            return (
              <button
                key={ch._id}
                onClick={() => {
                  navigate(`/read/${bookId}/${ch.sequence}`);
                  onClose();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`w-full flex items-center gap-4 p-4 rounded-2xl text-left transition-all ${
                  isCurrent 
                    ? 'bg-[var(--accent-soft)] ring-1 ring-[var(--accent)]/30' 
                    : 'hover:bg-[var(--bg-tertiary)]'
                } ${!isUnlocked ? 'opacity-50' : ''}`}
              >
                <span className={`font-ui text-xs font-bold w-6 ${
                  isCurrent ? 'text-[var(--accent)]' : 'text-[var(--text-tertiary)]'
                }`}>
                  {String(ch.sequence).padStart(2, '0')}
                </span>
                
                <span className={`font-body text-sm flex-1 truncate ${
                  isCurrent ? 'text-[var(--text-primary)] font-medium' : 'text-[var(--text-secondary)]'
                }`}>
                  {ch.title}
                </span>
                
                {!isUnlocked && (
                  <span className="flex items-center gap-1 font-ui text-[10px] text-[var(--accent)]">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2"/>
                      <text x="12" y="16" textAnchor="middle" fontSize="8" fontWeight="bold">L</text>
                    </svg>
                    {ch.tokenCost}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
